import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PRESALE_VISIBLE } from '@/config/stripe';
import { usePresaleAccess } from '@/hooks/usePresaleAccess';
import {
  SALES_AGREEMENT,
  SALES_AGREEMENT_EFFECTIVE,
  SALES_AGREEMENT_VERSION,
  type AgreementBlock,
} from '@/content/salesAgreement';

// The Sales Agreement a buyer accepts at checkout (§16). The text lives in
// src/content/salesAgreement.ts and is rendered verbatim here.
//
// Same gate as the checkout: until PRESALE_VISIBLE flips, only founders and
// presale_tester accounts see the agreement, because it carries the presale
// prices and those stay off the public site until the day purchase is possible.
// Once PRESALE_VISIBLE is true this page is public and indexable.

// A paragraph that opens with a short run-in heading ("Nature of Visits. …")
// gets that lead set in bold, matching the signed document.
const LEAD = /^([A-Z][A-Za-z'’-]*[;,]?(?:\s[A-Za-z][A-Za-z'’-]*[;,]?){0,2}\.)\s(?=[A-Z"“(])/;

function Paragraph({ text }: { text: string }) {
  const m = text.match(LEAD);
  if (!m) return <p>{text}</p>;
  return (
    <p>
      <strong>{m[1]}</strong> {text.slice(m[0].length)}
    </p>
  );
}

function Block({ block }: { block: AgreementBlock }) {
  if (block.type === 'sub') return <h3>{block.text}</h3>;
  if (block.type === 'li') {
    const m = block.text.match(LEAD);
    return (
      <li>
        {m ? (
          <>
            <strong>{m[1]}</strong> {block.text.slice(m[0].length)}
          </>
        ) : (
          block.text
        )}
      </li>
    );
  }
  return <Paragraph text={block.text} />;
}

const SalesAgreementPage = () => {
  const { canCheckout, checking } = usePresaleAccess();
  const visible = PRESALE_VISIBLE || canCheckout;

  return (
    <div className="min-h-screen bg-maslow-cream">
      <Helmet>
        <title>Sales Agreement | Maslow</title>
        <link rel="canonical" href="https://maslow.nyc/sales-agreement" />
        {!PRESALE_VISIBLE && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>

      {/* Header */}
      <div className="bg-maslow-navy py-16">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-serif font-bold text-white text-center"
          >
            Sales Agreement
          </motion.h1>
          {visible && (
            <p className="mt-4 text-center text-white/70 text-sm tracking-wide">
              Version 3 · Effective {SALES_AGREEMENT_EFFECTIVE} · Maslow LLC
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <style>{`
          .agreement, .agreement * { color: var(--navy); }
          .agreement a { color: var(--gold); text-decoration: underline; }
          .agreement h2 { font-weight: bold; font-size: 1.25rem; margin-top: 2.25em; margin-bottom: 0.6em; }
          .agreement h2:first-child { margin-top: 0; }
          .agreement h3 { font-weight: bold; margin-top: 1.4em; margin-bottom: 0.3em; }
          .agreement p { margin-bottom: 1em; line-height: 1.7; }
          .agreement ol { list-style: lower-alpha; margin-left: 1.5em; margin-bottom: 1em; }
          .agreement li { margin-bottom: 0.8em; line-height: 1.7; padding-left: 0.25em; }
          .agreement .meta { font-size: 0.875rem; opacity: 0.7; margin-bottom: 2.5em; }
        `}</style>

        {checking ? null : !visible ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="agreement">
            <p>
              The Maslow Sales Agreement is published on this page when the presale opens. Until then, nothing is for
              sale and there is nothing to agree to.
            </p>
            <p>
              Questions: <a href="mailto:hello@maslow.nyc">hello@maslow.nyc</a>. See also the{' '}
              <Link to="/terms-of-service">Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="agreement"
          >
            <p className="meta">
              This is the agreement you accept when you buy a presale tier. Read it before you check the box at
              checkout. It incorporates the <Link to="/terms-of-service">Terms of Service</Link> and{' '}
              <Link to="/privacy-policy">Privacy Policy</Link>. Agreement version {SALES_AGREEMENT_VERSION}.
            </p>

            {SALES_AGREEMENT.map((section) => {
              const items = section.blocks.filter((b) => b.type === 'li');
              const rest = section.blocks.filter((b) => b.type !== 'li');
              return (
                <section key={section.number} id={`section-${section.number}`}>
                  <h2>
                    {section.number}. {section.title}
                  </h2>
                  {rest.map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                  {items.length > 0 && (
                    <ol>
                      {items.map((b, i) => (
                        <Block key={i} block={b} />
                      ))}
                    </ol>
                  )}
                </section>
              );
            })}

            <p className="meta" style={{ marginTop: '3em' }}>
              Maslow LLC · New York · <a href="mailto:support@maslow.nyc">support@maslow.nyc</a>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SalesAgreementPage;
